import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { PAYMENTS_LIST_PAGE_STYLES } from './payments-list-page.utils';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { FinancialAccountSelectorComponent } from '../../shared/design-system/financial-account-selector.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { FinanceApiService } from '../finance/finance-api.service';
import {
  FinancialCompletenessDashboardRow,
  FunderProfileRow,
  InvestorPayableRow,
  PaymentsAdminService,
  PostingReadinessCheckRow,
  ReconciliationFindingRow,
  ReconciliationRunRow
} from './payments-admin.service';

@Component({
  standalone: true,
  selector: 'ain-payments-financial-completeness',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TabsModule,
    TagModule,
    TextareaModule,
    RouterLink,
    AinCanAccessDirective,
    CurrencySelectorComponent,
    FinancialAccountSelectorComponent,
    AinAmountPipe
  ],
  template: `
    <ain-page
      [eyebrow]="t('payments.eyebrow')"
      [title]="t('payments.financialCompleteness.title')"
      [description]="t('payments.financialCompleteness.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'payments.financialCompleteness.help.page.title'"
        [descriptionKey]="'payments.financialCompleteness.help.page.description'"
        [bulletKeys]="[
          'payments.financialCompleteness.help.page.bullets.dashboard',
          'payments.financialCompleteness.help.page.bullets.runs',
          'payments.financialCompleteness.help.page.bullets.payables'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('payments.financialCompleteness.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('payments.financialCompleteness.unavailable')" [message]="error() ?? ''" />
      } @else {
        <section class="card panel">
      <section class="toolbar">
        <div class="section-title">
          <span>{{ t('payments.financialCompleteness.help.toolbar.title') }}</span>
          <ain-info-hint
            [titleKey]="'payments.financialCompleteness.help.toolbar.title'"
            [descriptionKey]="'payments.financialCompleteness.help.toolbar.description'" />
        </div>
        <button pButton type="button" [label]="t('common.refresh')" (click)="reload()"></button>
        <a pButton type="button" [routerLink]="['/payments/portfolio']" [label]="t('payments.financialCompleteness.linkPortfolio')" class="p-button-outlined"></a>
        <a pButton type="button" [routerLink]="['/catalog/operations/control-room']" [label]="t('payments.financialCompleteness.linkPostingConfig')" class="p-button-outlined"></a>
      </section>

      @if (actionError()) {
        <ain-state type="error" [title]="t('payments.financialCompleteness.actionError')" [message]="actionError() ?? ''" />
      }

        <p-tabs value="dashboard">
          <p-tablist>
            <p-tab value="dashboard">{{ t('payments.financialCompleteness.tabs.dashboard') }}</p-tab>
            <p-tab value="runs">{{ t('payments.financialCompleteness.tabs.runs') }}</p-tab>
            <p-tab value="findings">{{ t('payments.financialCompleteness.tabs.findings') }}</p-tab>
            <p-tab value="payables">{{ t('payments.financialCompleteness.tabs.payables') }}</p-tab>
            <p-tab value="readiness">{{ t('payments.financialCompleteness.tabs.readiness') }}</p-tab>
            <p-tab value="legacy">{{ t('payments.financialCompleteness.tabs.legacy') }}</p-tab>
          </p-tablist>
          <p-tabpanels>
            <p-tabpanel value="dashboard">
              @if (dashboard()) {
                <div class="section-title">
                  <span>{{ t('payments.financialCompleteness.tabs.dashboard') }}</span>
                  <ain-info-hint
                    [titleKey]="'payments.financialCompleteness.help.tabs.dashboard.title'"
                    [descriptionKey]="'payments.financialCompleteness.help.tabs.dashboard.description'" />
                </div>
                <section class="summary-grid">
                  @for (card of dashboardCards(); track card.key) {
                    <div>
                      <span>{{ card.label }}</span>
                      <strong>{{ card.value | ainAmount }}</strong>
                    </div>
                  }
                </section>
                <h4>{{ t('payments.financialCompleteness.findingsByType') }}</h4>
                <p-table [value]="findingTypeRows()">
                    <ng-template pTemplate="header">
                      <tr>
                        <th>{{ t('payments.financialCompleteness.findingType') }}</th>
                        <th>{{ t('payments.financialCompleteness.count') }}</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                      <tr>
                        <td><code>{{ row.type }}</code></td>
                        <td>{{ row.count | ainAmount }}</td>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                      <tr><td colspan="2">{{ t('payments.financialCompleteness.emptyFindingsByType') }}</td></tr>
                    </ng-template>
                  </p-table>
              }
            </p-tabpanel>

            <p-tabpanel value="runs">
              <div class="section-title">
                <span>{{ t('payments.financialCompleteness.tabs.runs') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.financialCompleteness.help.tabs.runs.title'"
                  [descriptionKey]="'payments.financialCompleteness.help.tabs.runs.description'" />
              </div>
              <div class="toolbar" ainCanAccess [ainCanAccessPermission]="'payments.portfolio.update'">
                <button pButton type="button" [label]="t('payments.financialCompleteness.startRun')" (click)="openStartRunDialog()"></button>
              </div>
              <p-table
                [value]="runs()"
                [paginator]="true"
                [rows]="runPageSize"
                [totalRecords]="runTotalRecords"
                [lazy]="true"
                (onLazyLoad)="onRunPage($event)">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('payments.financialCompleteness.scope') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('payments.financialCompleteness.period') }}</th>
                    <th>{{ t('payments.financialCompleteness.startedAt') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.scope }}</td>
                    <td><p-tag [value]="row.status" [severity]="runSeverity(row.status)"></p-tag></td>
                    <td>{{ row.periodFromUtc }} → {{ row.periodToUtc }}</td>
                    <td>{{ row.startedAtUtc ?? '—' }}</td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr><td colspan="4">{{ t('payments.financialCompleteness.emptyRuns') }}</td></tr>
                </ng-template>
              </p-table>
            </p-tabpanel>

            <p-tabpanel value="findings">
              <div class="section-title">
                <span>{{ t('payments.financialCompleteness.tabs.findings') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.financialCompleteness.help.tabs.findings.title'"
                  [descriptionKey]="'payments.financialCompleteness.help.tabs.findings.description'" />
              </div>
              <section class="toolbar">
                <p-select [options]="findingStatusOptions" [(ngModel)]="findingStatus" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body" [placeholder]="t('columns.status')"></p-select>
                <p-select [options]="findingSeverityOptions" [(ngModel)]="findingSeverity" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body" [placeholder]="t('payments.financialCompleteness.severity')"></p-select>
                <input pInputText [(ngModel)]="findingTypeFilter" [placeholder]="t('payments.financialCompleteness.findingType')" />
                <button pButton type="button" [label]="t('common.filter')" (click)="loadFindings()"></button>
              </section>
              <p-table
                [value]="findings()"
                dataKey="id"
                [paginator]="true"
                [rows]="findingPageSize"
                [totalRecords]="findingTotalRecords"
                [lazy]="true"
                (onLazyLoad)="onFindingPage($event)">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('payments.financialCompleteness.severity') }}</th>
                    <th>{{ t('payments.financialCompleteness.findingType') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('payments.financialCompleteness.source') }}</th>
                    <th ainCanAccess [ainCanAccessPermission]="'payments.portfolio.update'">{{ t('payments.portfolio.actions') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr
                    class="selectable-row"
                    [class.selected-row]="selectedFinding()?.id === row.id"
                    (click)="selectedFinding.set(row)">
                    <td><p-tag [value]="row.severity" [severity]="severityTag(row.severity)"></p-tag></td>
                    <td><code>{{ row.findingType }}</code></td>
                    <td><p-tag [value]="row.status"></p-tag></td>
                    <td>{{ row.sourceModule }} / {{ row.sourceEntityName }}</td>
                    <td class="actions" ainCanAccess [ainCanAccessPermission]="'payments.portfolio.update'">
                      @if (row.status === 'Open') {
                        <button pButton type="button" size="small" [label]="t('payments.financialCompleteness.acknowledge')" (click)="acknowledgeFinding(row); $event.stopPropagation()"></button>
                      }
                      @if (row.status !== 'Resolved') {
                        <button pButton type="button" size="small" severity="success" [label]="t('payments.financialCompleteness.resolve')" (click)="selectFindingForResolve(row); $event.stopPropagation()"></button>
                      }
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr><td colspan="5">{{ t('payments.financialCompleteness.emptyFindings') }}</td></tr>
                </ng-template>
              </p-table>

              @if (selectedFinding()) {
                <section class="detail-card">
                  <h4>{{ t('payments.financialCompleteness.findingDetail') }}</h4>
                  <p>{{ t('payments.financialCompleteness.evidence') }}: <code>{{ selectedFinding()!.evidenceJson }}</code></p>
                </section>
              }
            </p-tabpanel>

            <p-tabpanel value="payables">
              <div class="section-title">
                <span>{{ t('payments.financialCompleteness.tabs.payables') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.financialCompleteness.help.tabs.payables.title'"
                  [descriptionKey]="'payments.financialCompleteness.help.tabs.payables.description'" />
              </div>
              <section class="notice-card">
                <h4>{{ t('payments.financialCompleteness.payoutDeferredTitle') }}</h4>
                <p>{{ t('payments.financialCompleteness.payoutDeferredMessage') }}</p>
              </section>
              <section class="toolbar">
                <p-select [options]="funderOptions()" [(ngModel)]="payableFunderId" optionLabel="label" optionValue="value" [showClear]="true" [filter]="true" appendTo="body" [placeholder]="t('payments.portfolio.filterFunder')"></p-select>
                <p-select [options]="payableStatusOptions" [(ngModel)]="payableStatus" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body" [placeholder]="t('columns.status')"></p-select>
                <ain-currency-selector [(ngModel)]="payableCurrency"></ain-currency-selector>
                <button pButton type="button" [label]="t('common.filter')" (click)="loadPayables()"></button>
              </section>
              <p-table
                [value]="payables()"
                dataKey="id"
                [paginator]="true"
                [rows]="payablePageSize"
                [totalRecords]="payableTotalRecords"
                [lazy]="true"
                (onLazyLoad)="onPayablePage($event)">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('payments.portfolio.filterFunder') }}</th>
                    <th>{{ t('payments.portfolio.amount') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('payments.financialCompleteness.createdAt') }}</th>
                    <th ainCanAccess [ainCanAccessPermission]="'payments.portfolio.update'">{{ t('payments.portfolio.actions') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr
                    class="selectable-row"
                    [class.selected-row]="selectedPayable()?.id === row.id"
                    (click)="selectedPayable.set(row)">
                    <td>{{ funderLabel(row.funderId) }}</td>
                    <td>{{ row.amount | ainAmount }} {{ row.currencyCode }}</td>
                    <td><p-tag [value]="row.status"></p-tag></td>
                    <td>{{ row.createdAtUtc }}</td>
                    <td class="actions" ainCanAccess [ainCanAccessPermission]="'payments.portfolio.update'">
                      @if (row.status === 'Pending') {
                        <button pButton type="button" size="small" [label]="t('payments.financialCompleteness.approve')" (click)="approvePayable(row); $event.stopPropagation()"></button>
                      }
                      @if (row.status === 'Approved') {
                        <button pButton type="button" size="small" severity="success" [label]="t('payments.financialCompleteness.markPaid')" (click)="selectPayableForPaid(row); $event.stopPropagation()"></button>
                      }
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr><td colspan="5">{{ t('payments.financialCompleteness.emptyPayables') }}</td></tr>
                </ng-template>
              </p-table>

            </p-tabpanel>

            <p-tabpanel value="readiness">
              <div class="section-title">
                <span>{{ t('payments.financialCompleteness.tabs.readiness') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.financialCompleteness.help.tabs.readiness.title'"
                  [descriptionKey]="'payments.financialCompleteness.help.tabs.readiness.description'" />
              </div>
              <p-table [value]="readiness()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('payments.financialCompleteness.flow') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('payments.financialCompleteness.detail') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ t('payments.financialCompleteness.flows.' + row.flowKey) }}</td>
                    <td><p-tag [value]="row.status" [severity]="row.status === 'Ready' ? 'success' : 'danger'"></p-tag></td>
                    <td>{{ row.detailKey ? t(row.detailKey) : t('payments.financialCompleteness.readinessReady') }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </p-tabpanel>

            <p-tabpanel value="legacy">
              <div class="section-title">
                <span>{{ t('payments.financialCompleteness.tabs.legacy') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.financialCompleteness.help.tabs.legacy.title'"
                  [descriptionKey]="'payments.financialCompleteness.help.tabs.legacy.description'" />
              </div>
              <section class="notice-card">
                <h4>{{ t('payments.financialCompleteness.legacyTitle') }}</h4>
                <p>{{ t('payments.financialCompleteness.legacyMessage') }}</p>
              </section>
              @if (dashboard()?.legacyPaymentsLendingParallelActive) {
                <p-tag severity="warn" [value]="t('payments.financialCompleteness.legacyActive')"></p-tag>
              } @else {
                <p-tag severity="success" [value]="t('payments.financialCompleteness.legacyClear')"></p-tag>
              }
            </p-tabpanel>
          </p-tabpanels>
        </p-tabs>
        </section>
      }

      <p-dialog
        [header]="t('payments.financialCompleteness.startRunTitle')"
        [modal]="true"
        [(visible)]="startRunDialogVisible"
        [style]="{ width: '560px' }">
        <div class="dialog-grid">
          <label class="wide">
            <span class="label-row">
              {{ t('payments.financialCompleteness.scope') }}
              <ain-info-hint
                [titleKey]="'payments.financialCompleteness.help.startRun.title'"
                [descriptionKey]="'payments.financialCompleteness.help.startRun.description'" />
            </span>
            <p-select
              [options]="scopeOptions"
              [(ngModel)]="runScope"
              name="runScope"
              optionLabel="label"
              optionValue="value"
              appendTo="body"
              [placeholder]="t('payments.financialCompleteness.scope')">
            </p-select>
          </label>
          <label>
            <span>{{ t('payments.financialCompleteness.periodFrom') }}</span>
            <input pInputText type="datetime-local" [(ngModel)]="runPeriodFrom" name="runPeriodFrom" />
          </label>
          <label>
            <span>{{ t('payments.financialCompleteness.periodTo') }}</span>
            <input pInputText type="datetime-local" [(ngModel)]="runPeriodTo" name="runPeriodTo" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="startRunDialogVisible = false"></button>
          <button pButton type="button" [label]="t('payments.financialCompleteness.startRun')" [disabled]="startingRun()" (click)="startRun()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog
        [header]="t('payments.financialCompleteness.resolveTitle')"
        [modal]="true"
        [(visible)]="resolveDialogVisible"
        [style]="{ width: '560px' }">
        <div class="dialog-grid">
          <label class="wide">
            <span class="label-row">
              {{ t('payments.financialCompleteness.resolveEvidence') }}
              <ain-info-hint
                [titleKey]="'payments.financialCompleteness.help.resolve.title'"
                [descriptionKey]="'payments.financialCompleteness.help.resolve.description'" />
            </span>
            <textarea pTextarea rows="4" [(ngModel)]="resolveEvidenceJson" name="resolveEvidenceJson"></textarea>
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="resolveDialogVisible = false"></button>
          <button pButton type="button" [label]="t('payments.financialCompleteness.confirmResolve')" [disabled]="resolvingFinding()" (click)="resolveFinding()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog
        [header]="t('payments.financialCompleteness.markPaidTitle')"
        [modal]="true"
        [(visible)]="markPaidDialogVisible"
        [style]="{ width: '640px' }">
        <div class="dialog-grid">
          <label class="wide">
            <span class="label-row">
              {{ t('payments.financialCompleteness.markPaidPosting') }}
              <ain-info-hint
                [titleKey]="'payments.financialCompleteness.help.markPaid.title'"
                [descriptionKey]="'payments.financialCompleteness.help.markPaid.description'" />
            </span>
            <p-select [options]="bookOptions()" [(ngModel)]="postingBookId" name="postingBookId" optionLabel="label" optionValue="value" appendTo="body" (ngModelChange)="onBookChanged()" [placeholder]="t('finance.journal.book')"></p-select>
          </label>
          <label class="wide">
            <span>{{ t('finance.journal.period') }}</span>
            <p-select [options]="periodOptions()" [(ngModel)]="postingPeriodId" name="postingPeriodId" optionLabel="label" optionValue="value" appendTo="body" [placeholder]="t('finance.journal.period')"></p-select>
          </label>
          <label class="wide">
            <span>{{ t('payments.financialCompleteness.debitAccount') }}</span>
            <ain-financial-account-selector [(ngModel)]="postingDebitAccountId" name="postingDebitAccountId" [bookId]="postingBookId"></ain-financial-account-selector>
          </label>
          <label class="wide">
            <span>{{ t('payments.financialCompleteness.creditAccount') }}</span>
            <ain-financial-account-selector [(ngModel)]="postingCreditAccountId" name="postingCreditAccountId" [bookId]="postingBookId"></ain-financial-account-selector>
          </label>
          <label>
            <span>{{ t('columns.currency') }}</span>
            <ain-currency-selector [(ngModel)]="postingCurrencyCode" name="postingCurrencyCode"></ain-currency-selector>
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="markPaidDialogVisible = false"></button>
          <button pButton type="button" [label]="t('payments.financialCompleteness.confirmMarkPaid')" [disabled]="markingPaid()" (click)="markPaid()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .label-row { display: inline-flex; align-items: center; gap: 6px; }
    ${PAYMENTS_LIST_PAGE_STYLES}
    .toolbar { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; align-items: center; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; }
    .detail-card { margin-top: 12px; padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; display: flex; flex-direction: column; gap: 8px; }
    .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 12px; margin-bottom: 16px; }
    .summary-grid div { padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; display: flex; flex-direction: column; gap: 4px; }
    .summary-grid span { color: #64748b; font-size: 12px; }
    .notice-card { margin-bottom: 12px; padding: 12px; border: 1px solid #bfdbfe; background: #eff6ff; border-radius: 8px; }
    .notice-card h4 { margin: 0 0 6px; font-size: 14px; }
    .notice-card p { margin: 0; color: #334155; }
    .selectable-row { cursor: pointer; }
    .selected-row { background: #eff6ff; }
  `]
})
export class PaymentsFinancialCompletenessComponent implements OnInit {
  private readonly api = inject(PaymentsAdminService);
  private readonly financeApi = inject(FinanceApiService);
  private readonly localizationService = inject(LocalizationService);

  readonly dashboard = signal<FinancialCompletenessDashboardRow | null>(null);
  readonly runs = signal<ReconciliationRunRow[]>([]);
  readonly findings = signal<ReconciliationFindingRow[]>([]);
  readonly payables = signal<InvestorPayableRow[]>([]);
  readonly readiness = signal<PostingReadinessCheckRow[]>([]);
  readonly funders = signal<FunderProfileRow[]>([]);
  readonly selectedFinding = signal<ReconciliationFindingRow | null>(null);
  readonly selectedPayable = signal<InvestorPayableRow | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly resolveFindingId = signal<string | null>(null);
  readonly markPaidPayableId = signal<string | null>(null);
  readonly startingRun = signal(false);
  readonly resolvingFinding = signal(false);
  readonly markingPaid = signal(false);

  startRunDialogVisible = false;
  resolveDialogVisible = false;
  markPaidDialogVisible = false;

  runScope = 'Marketplace';
  runPeriodFrom = '';
  runPeriodTo = '';
  runPage = 1;
  runPageSize = 25;
  runTotalRecords = 0;
  findingPage = 1;
  findingPageSize = 25;
  findingTotalRecords = 0;
  findingStatus = '';
  findingSeverity = '';
  findingTypeFilter = '';
  payablePage = 1;
  payablePageSize = 25;
  payableTotalRecords = 0;
  payableFunderId: string | null = null;
  payableStatus = '';
  payableCurrency = 'IRR';
  resolveEvidenceJson = '{"operator":"admin"}';
  postingBookId = '';
  postingPeriodId = '';
  postingDebitAccountId = '';
  postingCreditAccountId = '';
  postingCurrencyCode = 'IRR';
  bookOptions = signal<{ label: string; value: string }[]>([]);
  periodOptions = signal<{ label: string; value: string }[]>([]);

  readonly scopeOptions = [
    { label: 'Marketplace', value: 'Marketplace' },
    { label: 'Wallet', value: 'Wallet' },
    { label: 'Bnpl', value: 'Bnpl' },
    { label: 'Investor', value: 'Investor' },
    { label: 'Settlement', value: 'Settlement' },
    { label: 'Escrow', value: 'Escrow' }
  ];

  readonly findingStatusOptions = [
    { label: 'Open', value: 'Open' },
    { label: 'Acknowledged', value: 'Acknowledged' },
    { label: 'Resolved', value: 'Resolved' }
  ];

  readonly findingSeverityOptions = [
    { label: 'Info', value: 'Info' },
    { label: 'Warning', value: 'Warning' },
    { label: 'Critical', value: 'Critical' }
  ];

  readonly payableStatusOptions = [
    { label: 'Pending', value: 'Pending' },
    { label: 'Approved', value: 'Approved' },
    { label: 'Paid', value: 'Paid' },
    { label: 'Cancelled', value: 'Cancelled' }
  ];

  readonly dashboardCards = computed(() => {
    const d = this.dashboard();
    if (!d) return [];
    return [
      { key: 'open', label: this.t('payments.financialCompleteness.openFindings'), value: d.reconciliation.openFindings },
      { key: 'critical', label: this.t('payments.financialCompleteness.criticalFindings'), value: d.reconciliation.criticalFindings },
      { key: 'failedRuns', label: this.t('payments.financialCompleteness.failedRuns'), value: d.failedReconciliationRuns },
      { key: 'pendingPayables', label: this.t('payments.financialCompleteness.pendingPayables'), value: d.investorPayables.pendingCount },
      { key: 'pendingAmount', label: this.t('payments.financialCompleteness.pendingAmount'), value: d.investorPayables.pendingAmount },
      { key: 'escrow', label: this.t('payments.financialCompleteness.escrowMismatches'), value: d.reconciliation.findingsByType['escrow_wallet_settlement_alignment_mismatch'] ?? 0 }
    ];
  });

  readonly findingTypeRows = computed(() => {
    const types = this.dashboard()?.reconciliation.findingsByType ?? {};
    return Object.entries(types).map(([type, count]) => ({ type, count }));
  });

  ngOnInit(): void {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    this.runPeriodFrom = weekAgo.toISOString().slice(0, 16);
    this.runPeriodTo = now.toISOString().slice(0, 16);
    this.financeApi.listBooks().subscribe({
      next: books => {
        this.bookOptions.set(books.map(book => ({ label: `${book.code} — ${book.name}`, value: book.id })));
        this.postingBookId = books[0]?.id ?? '';
        if (this.postingBookId) this.onBookChanged();
      }
    });
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.actionError.set(null);

    this.api.getFinancialCompletenessDashboard().subscribe({
      next: row => {
        this.dashboard.set(row);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('payments.financialCompleteness.errorLoad'));
        this.loading.set(false);
      }
    });
    this.loadRuns();
    this.loadFindings();
    this.loadPayables();
    this.api.getPostingReadiness().subscribe({
      next: rows => this.readiness.set(rows),
      error: () => { /* optional tab */ }
    });
    this.api.listFunderProfiles().subscribe({
      next: rows => this.funders.set(rows),
      error: () => { /* optional for labels */ }
    });
  }

  loadRuns(): void {
    this.api.listReconciliationRuns({ page: this.runPage, pageSize: this.runPageSize }).subscribe({
      next: response => {
        this.runs.set(response.items);
        this.runTotalRecords = response.totalCount;
      },
      error: () => this.actionError.set(this.t('payments.financialCompleteness.errorLoad'))
    });
  }

  loadFindings(): void {
    this.api.listReconciliationFindings({
      status: this.findingStatus || undefined,
      severity: this.findingSeverity || undefined,
      findingType: this.findingTypeFilter || undefined,
      page: this.findingPage,
      pageSize: this.findingPageSize
    }).subscribe({
      next: response => {
        this.findings.set(response.items);
        this.findingTotalRecords = response.totalCount;
      },
      error: () => this.error.set(this.t('payments.financialCompleteness.errorLoad'))
    });
  }

  loadPayables(): void {
    this.api.listInvestorPayables({
      funderId: this.payableFunderId ?? undefined,
      status: this.payableStatus || undefined,
      currencyCode: this.payableCurrency || undefined,
      page: this.payablePage,
      pageSize: this.payablePageSize
    }).subscribe({
      next: response => {
        this.payables.set(response.items);
        this.payableTotalRecords = response.totalCount;
      },
      error: () => this.error.set(this.t('payments.financialCompleteness.errorLoad'))
    });
  }

  openStartRunDialog(): void {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    this.runPeriodFrom = weekAgo.toISOString().slice(0, 16);
    this.runPeriodTo = now.toISOString().slice(0, 16);
    this.actionError.set(null);
    this.startRunDialogVisible = true;
  }

  startRun(): void {
    const key = `admin-recon-${Date.now()}`;
    this.startingRun.set(true);
    this.api.startReconciliationRun({
      scope: this.runScope,
      periodFromUtc: new Date(this.runPeriodFrom).toISOString(),
      periodToUtc: new Date(this.runPeriodTo).toISOString(),
      idempotencyKey: key,
      idempotencyPayloadHash: key,
      correlationId: 'admin-financial-completeness'
    }).subscribe({
      next: () => {
        this.startRunDialogVisible = false;
        this.startingRun.set(false);
        this.reload();
      },
      error: err => {
        this.actionError.set(this.parseApiError(err) ?? this.t('payments.financialCompleteness.errorStartRun'));
        this.startingRun.set(false);
      }
    });
  }

  acknowledgeFinding(row: ReconciliationFindingRow): void {
    this.api.acknowledgeReconciliationFinding(row.id).subscribe({
      next: () => this.reload(),
      error: err => this.actionError.set(this.parseApiError(err) ?? this.t('payments.financialCompleteness.errorAcknowledge'))
    });
  }

  selectFindingForResolve(row: ReconciliationFindingRow): void {
    this.selectedFinding.set(row);
    this.resolveFindingId.set(row.id);
    this.resolveEvidenceJson = '{"operator":"admin"}';
    this.actionError.set(null);
    this.resolveDialogVisible = true;
  }

  resolveFinding(): void {
    const id = this.resolveFindingId();
    if (!id) return;
    this.resolvingFinding.set(true);
    this.api.resolveReconciliationFinding(id, this.resolveEvidenceJson).subscribe({
      next: () => {
        this.resolveFindingId.set(null);
        this.resolveDialogVisible = false;
        this.resolvingFinding.set(false);
        this.reload();
      },
      error: err => {
        this.actionError.set(this.parseApiError(err) ?? this.t('payments.financialCompleteness.errorResolve'));
        this.resolvingFinding.set(false);
      }
    });
  }

  approvePayable(row: InvestorPayableRow): void {
    this.api.approveInvestorPayable(row.id).subscribe({
      next: () => this.reload(),
      error: err => this.actionError.set(this.parseApiError(err) ?? this.t('payments.financialCompleteness.errorApprove'))
    });
  }

  selectPayableForPaid(row: InvestorPayableRow): void {
    this.selectedPayable.set(row);
    this.markPaidPayableId.set(row.id);
    this.postingCurrencyCode = row.currencyCode;
    this.actionError.set(null);
    this.markPaidDialogVisible = true;
  }

  markPaid(): void {
    const id = this.markPaidPayableId();
    if (!id || !this.postingBookId || !this.postingPeriodId || !this.postingDebitAccountId || !this.postingCreditAccountId) {
      this.actionError.set(this.t('payments.financialCompleteness.errorPostingProfile'));
      return;
    }
    this.markingPaid.set(true);
    this.api.markInvestorPayablePaid(id, {
      idempotencyKey: `admin-payable-paid-${id}`,
      postingProfile: {
        bookId: this.postingBookId,
        periodId: this.postingPeriodId,
        debitAccountId: this.postingDebitAccountId,
        creditAccountId: this.postingCreditAccountId,
        currencyCode: this.postingCurrencyCode
      }
    }).subscribe({
      next: () => {
        this.markPaidPayableId.set(null);
        this.markPaidDialogVisible = false;
        this.markingPaid.set(false);
        this.reload();
      },
      error: err => {
        this.actionError.set(this.parseApiError(err) ?? this.t('payments.financialCompleteness.errorMarkPaid'));
        this.markingPaid.set(false);
      }
    });
  }

  onBookChanged(): void {
    if (!this.postingBookId) return;
    this.financeApi.listFiscalPeriods(this.postingBookId).subscribe({
      next: periods => {
        this.periodOptions.set(periods.map(period => ({
          label: `${period.fiscalYear}/${period.periodNumber} (${period.status})`,
          value: period.id
        })));
        this.postingPeriodId = periods.find(item => item.status.toLowerCase() === 'open')?.id ?? periods[0]?.id ?? '';
      }
    });
  }

  onRunPage(event: TableLazyLoadEvent): void {
    const rows = event.rows ?? this.runPageSize;
    const page = Math.floor((event.first ?? 0) / rows) + 1;
    if (page === this.runPage && rows === this.runPageSize) {
      return;
    }

    this.runPage = page;
    this.runPageSize = rows;
    this.loadRuns();
  }

  onFindingPage(event: TableLazyLoadEvent): void {
    const rows = event.rows ?? this.findingPageSize;
    const page = Math.floor((event.first ?? 0) / rows) + 1;
    if (page === this.findingPage && rows === this.findingPageSize) {
      return;
    }

    this.findingPage = page;
    this.findingPageSize = rows;
    this.loadFindings();
  }

  onPayablePage(event: TableLazyLoadEvent): void {
    const rows = event.rows ?? this.payablePageSize;
    const page = Math.floor((event.first ?? 0) / rows) + 1;
    if (page === this.payablePage && rows === this.payablePageSize) {
      return;
    }

    this.payablePage = page;
    this.payablePageSize = rows;
    this.loadPayables();
  }

  funderOptions = computed(() =>
    this.funders().map(row => ({ label: `${row.funderCode} — ${row.displayName}`, value: row.id }))
  );

  funderLabel(funderId: string): string {
    const funder = this.funders().find(row => row.id === funderId);
    return funder ? `${funder.funderCode}` : funderId;
  }

  runSeverity(status: string): 'success' | 'warn' | 'danger' | 'info' {
    if (status === 'Completed') return 'success';
    if (status === 'Failed') return 'danger';
    if (status === 'Running') return 'warn';
    return 'info';
  }

  severityTag(severity: string): 'success' | 'warn' | 'danger' | 'info' {
    if (severity === 'Critical') return 'danger';
    if (severity === 'Warning') return 'warn';
    return 'info';
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }

  private parseApiError(err: unknown): string | null {
    const body = (err as { error?: { error?: string; message?: string } })?.error;
    return body?.error ?? body?.message ?? null;
  }
}
